'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { LocaleForm, type LocaleFormRef } from '@/components/partials/locales'
import { useCreateLocale } from '@/hooks'
import { useMetaStore } from '@/store'
import { CreateLocaleParams } from '@/types/locale'
import { FormModeEnum } from '@/enums'

const CreateLocale = () => {
  const router = useRouter()
  const t = useTranslations()
  const queryClient = useQueryClient()
  const localeFormRef = useRef<LocaleFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createLocale, isPending: isLoading } = useCreateLocale()
  const getMeta = useMetaStore(state => state.getMeta)

  const handleSubmit = (data: CreateLocaleParams) => {
    createLocale(data, {
      onSuccess: async newLocale => {
        setIsRedirecting(true)
        queryClient.setQueryData(['locale', newLocale.id], newLocale)
        await queryClient.invalidateQueries({ queryKey: ['locales'] })
        await getMeta()
        toast.success(t('created_successfully'))
        router.push(`/localization/locales/${newLocale.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateLocaleParams) => {
    createLocale(data, {
      onSuccess: async newLocale => {
        queryClient.setQueryData(['locale', newLocale.id], newLocale)
        await queryClient.invalidateQueries({ queryKey: ['locales'] })
        await getMeta()
        const detailUrl = `/localization/locales/${newLocale.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        localeFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (localeFormRef.current) {
        localeFormRef.current.handleValidationErrors(error)
      }
      return
    }
    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/localization/locales')
  }

  return (
    <LocaleForm
      key={formKey}
      ref={localeFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default CreateLocale
